import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Building2, Filter, Store, Star, MessageSquare, Calendar, TrendingUp, ThumbsUp, ThumbsDown } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { KPICard } from '../components/ui/KPICard';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
// Normal distribution helper
function generateNormalDistribution(mean: number, stdDev: number, totalCount: number): Array<{
  rating: number;
  count: number;
}> {
  const data: Array<{
    rating: number;
    count: number;
  }> = [];
  const rawValues: number[] = [];
  for (let r = 10; r <= 50; r++) {
    const rating = r / 10;
    const exponent = -0.5 * Math.pow((rating - mean) / stdDev, 2);
    const value = Math.exp(exponent);
    rawValues.push(value);
  }
  const rawSum = rawValues.reduce((a, b) => a + b, 0);
  for (let i = 0; i < rawValues.length; i++) {
    const rating = (i + 10) / 10;
    const count = Math.round(rawValues[i] / rawSum * totalCount);
    data.push({
      rating,
      count
    });
  }
  return data;
}
// Region filter options
const regions = [{
  id: 'konzern',
  name: 'Gesamtkonzern',
  icon: Building2
}, {
  id: 'nord',
  name: 'Fielmann Nord'
}, {
  id: 'minden-hannover',
  name: 'Fielmann Ost'
}, {
  id: 'rhein-ruhr',
  name: 'Fielmann West'
}, {
  id: 'hessenring',
  name: 'Fielmann Mitte'
}, {
  id: 'nordbayern',
  name: 'Fielmann Sachsen-Thüringen'
}, {
  id: 'suedwest',
  name: 'Fielmann Südwest'
}, {
  id: 'suedbayern',
  name: 'Fielmann Bayern'
}];
// Mock data per region
const regionData: Record<string, {
  marketCount: number;
  avgRating: number;
  reviewCount: number;
  pieData: Array<{
    name: string;
    value: number;
  }>;
  barData: Array<{
    rating: number;
    count: number;
  }>;
}> = {
  konzern: {
    marketCount: 900,
    avgRating: 4.3,
    reviewCount: 8420,
    pieData: [{
      name: '< 3.5',
      value: 320
    }, {
      name: '3.5 - 4.0',
      value: 680
    }, {
      name: '4.1 - 4.2',
      value: 1150
    }, {
      name: '4.3 - 4.4',
      value: 1480
    }, {
      name: '> 4.4',
      value: 1420
    }],
    barData: generateNormalDistribution(4.3, 0.5, 8420)
  },
  nord: {
    marketCount: 140,
    avgRating: 4.4,
    reviewCount: 1280,
    pieData: [{
      name: '< 3.5',
      value: 42
    }, {
      name: '3.5 - 4.0',
      value: 85
    }, {
      name: '4.1 - 4.2',
      value: 165
    }, {
      name: '4.3 - 4.4',
      value: 235
    }, {
      name: '> 4.4',
      value: 265
    }],
    barData: generateNormalDistribution(4.4, 0.45, 1280)
  },
  'minden-hannover': {
    marketCount: 120,
    avgRating: 4.2,
    reviewCount: 1050,
    pieData: [{
      name: '< 3.5',
      value: 58
    }, {
      name: '3.5 - 4.0',
      value: 115
    }, {
      name: '4.1 - 4.2',
      value: 168
    }, {
      name: '4.3 - 4.4',
      value: 188
    }, {
      name: '> 4.4',
      value: 140
    }],
    barData: generateNormalDistribution(4.2, 0.55, 1050)
  }
};
const getRegionData = (regionId: string) => {
  if (regionData[regionId]) return regionData[regionId];
  return {
    marketCount: 110,
    avgRating: 4.25,
    reviewCount: 980,
    pieData: [{
      name: '< 3.5',
      value: 48
    }, {
      name: '3.5 - 4.0',
      value: 95
    }, {
      name: '4.1 - 4.2',
      value: 135
    }, {
      name: '4.3 - 4.4',
      value: 175
    }, {
      name: '> 4.4',
      value: 165
    }],
    barData: generateNormalDistribution(4.25, 0.5, 980)
  };
};
// Region scorecard data
const regionScorecard = [{
  region: 'Nord',
  avgRating: 4.4,
  markets: 140,
  reviews: 1280
}, {
  region: 'Ost',
  avgRating: 4.2,
  markets: 120,
  reviews: 1050
}, {
  region: 'West',
  avgRating: 4.1,
  markets: 165,
  reviews: 1420
}, {
  region: 'Mitte',
  avgRating: 4.35,
  markets: 95,
  reviews: 850
}, {
  region: 'Sachsen-Thüringen',
  avgRating: 4.25,
  markets: 110,
  reviews: 920
}, {
  region: 'Südwest',
  avgRating: 4.3,
  markets: 130,
  reviews: 1150
}, {
  region: 'Bayern',
  avgRating: 4.5,
  markets: 140,
  reviews: 1250
}];
const timePeriods = [{
  id: '30',
  label: '30 Tage'
}, {
  id: '60',
  label: '60 Tage'
}, {
  id: '120',
  label: '120 Tage'
}, {
  id: '365',
  label: '365 Tage'
}, {
  id: 'gesamt',
  label: 'Gesamt'
}];
function applyTimeFactor(value: number, period: string, isRating: boolean = false): number {
  if (period === 'gesamt') return value;
  if (isRating) {
    const noise = period === '30' ? 0.1 : period === '60' ? -0.1 : period === '120' ? 0.05 : -0.05;
    return Math.max(1, Math.min(5, value + noise));
  }
  const factors: Record<string, number> = {
    '30': 0.08,
    '60': 0.17,
    '120': 0.33,
    '365': 1.0
  };
  return Math.max(1, Math.round(value * (factors[period] || 1)));
}
function getRatingColor(r: number): string {
  if (r > 4.4) return '#639754';
  if (r >= 4.3) return '#8BC34A';
  if (r >= 4.1) return '#c5a55a';
  if (r >= 3.5) return '#FF9800';
  return '#E03C32';
}
const PIE_COLORS = ['#E03C32', '#FF9800', '#c5a55a', '#8BC34A', '#639754'];
export function KonzernUebersichtPage() {
  const [selectedRegion, setSelectedRegion] = useState('konzern');
  const [selectedPeriod, setSelectedPeriod] = useState('gesamt');
  const rawData = getRegionData(selectedRegion);
  const currentData = {
    ...rawData,
    avgRating: applyTimeFactor(rawData.avgRating, selectedPeriod, true),
    reviewCount: applyTimeFactor(rawData.reviewCount, selectedPeriod),
    pieData: rawData.pieData.map(d => ({
      ...d,
      value: applyTimeFactor(d.value, selectedPeriod)
    })),
    barData: rawData.barData.map(d => ({
      ...d,
      count: applyTimeFactor(d.count, selectedPeriod)
    }))
  };
  const scaledScorecard = regionScorecard.map(item => ({
    ...item,
    avgRating: applyTimeFactor(item.avgRating, selectedPeriod, true),
    reviews: applyTimeFactor(item.reviews, selectedPeriod)
  }));
  return <div className="p-6 space-y-6 overflow-y-auto h-[calc(100vh-64px)]" data-id="element-665">
      {/* Region Filter */}
      <Card className="!p-4" data-id="element-666">
        <div className="flex flex-col gap-4" data-id="element-667">
          <div className="flex items-center gap-4 flex-wrap" data-id="element-668">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700" data-id="element-669">
              <Filter size={16} data-id="element-670" />
              <span data-id="element-671">Region:</span>
            </div>
            <div className="flex flex-wrap gap-2" data-id="element-672">
              {regions.map(region => {
              const isSelected = selectedRegion === region.id;
              const Icon = region.icon;
              return <button key={region.id} onClick={() => setSelectedRegion(region.id)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${isSelected ? 'bg-edeka-blue text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-673">
                    {Icon && <Icon size={14} data-id="element-674" />}
                    {region.name}
                  </button>;
            })}
            </div>
          </div>

          <div className="flex items-center gap-4 flex-wrap" data-id="element-675">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700" data-id="element-676">
              <Calendar size={16} data-id="element-677" />
              <span data-id="element-678">Zeitraum:</span>
            </div>
            <div className="flex flex-wrap gap-2" data-id="element-679">
              {timePeriods.map(period => {
              const isSelected = selectedPeriod === period.id;
              return <button key={period.id} onClick={() => setSelectedPeriod(period.id)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${isSelected ? 'bg-edeka-blue text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-680">
                    {period.label}
                  </button>;
            })}
            </div>
          </div>
        </div>
      </Card>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-681">
        <KPICard title="Durchschnittliche Bewertung" value={currentData.avgRating.toFixed(1)} icon={<TrendingUp size={18} data-id="element-683" />} data-id="element-682" />
        <KPICard title="Gesamtbewertungen" value={currentData.reviewCount.toLocaleString('de-DE')} icon={<MessageSquare size={18} data-id="element-685" />} data-id="element-684" />
        <KPICard title="Positive Resonanz" value="72%" icon={<ThumbsUp size={18} data-id="element-687" />} data-id="element-686" />
        <KPICard title="Negative Resonanz" value="8%" icon={<ThumbsDown size={18} data-id="element-689" />} data-id="element-688" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-690">
        {/* Pie Chart */}
        <Card data-id="element-691">
          <div className="p-6" data-id="element-692">
            <h3 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-693">
              Bewertungsverteilung
            </h3>
            <p className="text-sm text-gray-500 mb-6" data-id="element-694">
              Verteilung der Google Bewertungen nach Sterne-Kategorien
            </p>
            <div className="h-96" data-id="element-695">
              <ResponsiveContainer width="100%" height="100%" data-id="element-696">
                <PieChart data-id="element-697">
                  <Pie data={currentData.pieData} cx="50%" cy="60%" innerRadius={90} outerRadius={140} paddingAngle={2} dataKey="value" data-id="element-698">
                    {currentData.pieData.map((entry, index) => <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} data-id="element-699" />)}
                  </Pie>
                  <Tooltip contentStyle={{
                  borderRadius: '8px',
                  border: 'none',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  fontSize: '12px'
                }} formatter={(value: number) => [value.toLocaleString('de-DE') + ' Bewertungen', 'Anzahl']} data-id="element-700" />
                  <Legend layout="vertical" align="right" verticalAlign="middle" iconType="circle" iconSize={10} wrapperStyle={{
                  fontSize: '13px',
                  lineHeight: '2'
                }} formatter={(value: string) => <span style={{
                  color: '#1f2937',
                  fontWeight: 500
                }} data-id="element-702">
                        {value}
                      </span>} data-id="element-701" />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </Card>

        {/* Bar Chart */}
        <Card data-id="element-703">
          <div className="p-6" data-id="element-704">
            <h3 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-705">
              Bewertungen nach Sternen
            </h3>
            <p className="text-sm text-gray-500 mb-6" data-id="element-706">
              Verteilung der Bewertungen (1,0 bis 5,0 Sterne)
            </p>
            <div className="h-80" data-id="element-707">
              <ResponsiveContainer width="100%" height="100%" data-id="element-708">
                <BarChart data={currentData.barData} margin={{
                top: 20,
                right: 30,
                left: 0,
                bottom: 5
              }} data-id="element-709">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} data-id="element-710" />
                  <XAxis dataKey="rating" type="number" domain={[1, 5]} ticks={[1.0, 1.5, 2.0, 2.5, 3.0, 3.5, 4.0, 4.5, 5.0]} axisLine={false} tickLine={false} tick={{
                  fontSize: 11,
                  fill: '#6b7280'
                }} tickFormatter={(value: number) => value.toFixed(1).replace('.', ',')} data-id="element-711" />
                  <YAxis axisLine={false} tickLine={false} tick={{
                  fontSize: 12,
                  fill: '#9ca3af'
                }} tickFormatter={(value: number) => value >= 1000 ? `${(value / 1000).toFixed(1)}k` : String(value)} data-id="element-712" />
                  <Tooltip cursor={{
                  fill: '#f3f4f6'
                }} contentStyle={{
                  borderRadius: '8px',
                  border: 'none',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  fontSize: '12px'
                }} labelFormatter={(label: number) => `${label.toFixed(1).replace('.', ',')} Sterne`} formatter={(value: number) => [value.toLocaleString('de-DE'), 'Bewertungen']} data-id="element-713" />
                  <Bar dataKey="count" radius={[2, 2, 0, 0]} data-id="element-714">
                    {currentData.barData.map((entry, index) => {
                    const r = entry.rating;
                    let color = '#E03C32';
                    if (r > 4.4) color = '#639754';else if (r >= 4.3) color = '#8BC34A';else if (r >= 4.1) color = '#c5a55a';else if (r >= 3.5) color = '#FF9800';
                    return <Cell key={`cell-${index}`} fill={color} data-id="element-715" />;
                  })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </Card>
      </div>

      {/* Region Scorecard - Visual */}
      <Card data-id="element-716">
        <div className="p-6" data-id="element-717">
          <h3 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-718">
            Regionen Übersicht
          </h3>
          <p className="text-sm text-gray-500 mb-6" data-id="element-719">
            Kennzahlenvergleich aller Fielmann-Regionen
          </p>

          <div className="space-y-3" data-id="element-720">
            {[...scaledScorecard].sort((a, b) => b.avgRating - a.avgRating).map((item, index) => {
            const color = getRatingColor(item.avgRating);
            const barWidth = (item.avgRating - 3.0) / 2.0 * 100;
            const isTop = index === 0;
            return <motion.div key={item.region} initial={{
              opacity: 0,
              x: -12
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: index * 0.06
            }} className={`rounded-xl p-4 border ${isTop ? 'border-[#639754]/30 bg-[#f0f7ed]' : 'border-gray-100 bg-gray-50/50'}`} data-id="element-721">
                    <div className="flex items-center justify-between mb-2" data-id="element-722">
                      <div className="flex items-center gap-2.5" data-id="element-723">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold" style={{
                    backgroundColor: color
                  }} data-id="element-724">
                          {index + 1}
                        </div>
                        <span className="text-sm font-semibold text-gray-900" data-id="element-725">
                          {item.region}
                        </span>
                      </div>
                      <div className="flex items-center gap-4" data-id="element-726">
                        <div className="text-right" data-id="element-727">
                          <div className="text-xs text-gray-400" data-id="element-728">Filialen</div>
                          <div className="text-sm font-semibold text-gray-700" data-id="element-729">
                            {item.markets.toLocaleString('de-DE')}
                          </div>
                        </div>
                        <div className="text-right" data-id="element-730">
                          <div className="text-xs text-gray-400" data-id="element-731">
                            Bewertungen
                          </div>
                          <div className="text-sm font-semibold text-gray-700" data-id="element-732">
                            {item.reviews.toLocaleString('de-DE')}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Rating Bar */}
                    <div className="flex items-center gap-3" data-id="element-733">
                      <div className="flex-1 h-6 bg-gray-200/60 rounded-full overflow-hidden" data-id="element-734">
                        <motion.div initial={{
                    width: 0
                  }} animate={{
                    width: `${Math.min(barWidth, 100)}%`
                  }} transition={{
                    duration: 0.6,
                    delay: index * 0.06 + 0.2,
                    ease: 'easeOut'
                  }} className="h-full rounded-full relative" style={{
                    backgroundColor: color
                  }} data-id="element-735">
                          {barWidth > 30 && <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs font-bold text-white" data-id="element-736">
                              {item.avgRating.toFixed(1).replace('.', ',')} ★
                            </span>}
                        </motion.div>
                      </div>
                      {barWidth <= 30 && <span className="text-sm font-bold whitespace-nowrap" style={{
                  color
                }} data-id="element-737">
                          {item.avgRating.toFixed(1).replace('.', ',')} ★
                        </span>}
                    </div>
                  </motion.div>;
          })}
          </div>
        </div>
      </Card>
    </div>;
}